import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectAttacking } from './selectors';
import { attack } from './actions';
import { selectActivePokemon, selectIsMainPhase, selectOpponent, selectOptions } from '../ActiveGame/selectors';
import { optionReason } from '../ActiveGame/options';
import { Button, Muted } from '../Controls';
import AttackPicker from './AttackPicker';
import ConfirmButton from '../ConfirmButton';
import { EnergyCost } from '../Energy';
import { MoveDamage, MoveLabel, MoveList, MoveName } from './styled';
import { attackChoice } from './choices';

const MoveButtonLabel = ({ move }) => (
  <MoveLabel>
    <EnergyCost types={move.get('cost').toJS()} />
    <MoveName>{move.get('name')}</MoveName>
    {move.get('damage_text') && <MoveDamage>{move.get('damage_text')}</MoveDamage>}
  </MoveLabel>
);

MoveButtonLabel.propTypes = {
  move: PropTypes.object.isRequired,
};

/**
 * The active Pokémon's attacks, each one tap and a confirm away. Attacking ends the turn. An attack that asks for a
 * pick, like Whirlpool's energy card or Lure's benched Pokémon, shows the choices first (AttackPicker).
 */
const AttackButton = ({ userId }) => {
  const dispatch = useDispatch();
  const attacking = useSelector(selectAttacking);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const isMainPhase = useSelector((state) => selectIsMainPhase(state, { userId }));
  const options = useSelector(selectOptions);
  const opponent = useSelector(selectOpponent);
  const [picking, setPicking] = useState(null);

  if (!isMainPhase || !activePokemon) {
    return null;
  }

  if (picking) {
    return (
      <AttackPicker
        move={picking.move}
        kind={picking.kind}
        opponent={opponent}
        onPick={(picks) => {
          setPicking(null);
          dispatch(attack(picking.move.get('id'), picks));
        }}
        onBack={() => setPicking(null)}
      />
    );
  }

  const attacks = activePokemon.getIn(['card', 'moves'])
    .filter(move => move.get('is_attack'))
    .sortBy(move => move.get('number'));

  return (
    <MoveList role="group" aria-label="Attacks">
      {attacks.map(move => {
        // Greyed out with the server's reason when it can't be used: energy, a condition, its requirements.
        const reason = optionReason(options && options.getIn(['attacks', String(move.get('id'))]));
        const choice = attackChoice(move, opponent);
        return (
          <React.Fragment key={move.get('id')}>
            {choice.pick ?
              <Button onClick={() => setPicking({ move, kind: choice.pick })} disabled={attacking || !!reason} $primary $block>
                <MoveButtonLabel move={move} />
              </Button> :
              <ConfirmButton
                onConfirm={() => dispatch(attack(move.get('id'), choice.picks))}
                disabled={attacking || !!reason}
                prompt={`Attack with ${move.get('name')}? This ends your turn.`}
                confirmLabel="Attack"
                primary
                block
              >
                <MoveButtonLabel move={move} />
              </ConfirmButton>}
            {reason && <Muted>{reason}</Muted>}
          </React.Fragment>
        );
      })}
    </MoveList>
  );
};

AttackButton.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default AttackButton;
